import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, AlertCircle, Clock, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  getAudioMatrixRows,
  type MatrixSupportLevel,
  type MatrixImplementationStatus,
} from '@/integrations/capabilities/memoryModalities';

interface FeatureSupportMatrixProps {
  feature?: 'audio' | 'text' | 'image' | 'video';
  className?: string;
}

const getSupportIcon = (support: MatrixSupportLevel) => {
  switch (support) {
    case 'full':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'limited':
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    case 'planned':
      return <Clock className="h-5 w-5 text-blue-500" />;
    case 'blocked':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'not_supported':
      return <HelpCircle className="h-5 w-5 text-gray-400" />;
    default:
      return <HelpCircle className="h-5 w-5 text-gray-400" />;
  }
};

const getSupportBadge = (support: MatrixSupportLevel, t: (k: string) => string) => {
  switch (support) {
    case 'full':
      return (
        <Badge variant="default" className="bg-green-500">
          {t('page.featureSupport.supportLevels.full')}
        </Badge>
      );
    case 'limited':
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-700">
          {t('page.featureSupport.supportLevels.limited')}
        </Badge>
      );
    case 'planned':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700">
          {t('page.featureSupport.supportLevels.planned')}
        </Badge>
      );
    case 'blocked':
      return <Badge variant="destructive">{t('page.featureSupport.supportLevels.blocked')}</Badge>;
    case 'not_supported':
      return (
        <Badge variant="secondary">{t('page.featureSupport.supportLevels.notSupported')}</Badge>
      );
    default:
      return <Badge variant="secondary">Unknown</Badge>;
  }
};

const getStatusBadge = (status: MatrixImplementationStatus, t: (k: string) => string) => {
  switch (status) {
    case 'live':
      return (
        <Badge variant="default" className="bg-green-500">
          {t('page.featureSupport.statuses.live')}
        </Badge>
      );
    case 'not_implemented':
      return <Badge variant="outline">{t('page.featureSupport.statuses.notImplemented')}</Badge>;
    case 'blocked':
      return <Badge variant="destructive">{t('page.featureSupport.statuses.blocked')}</Badge>;
    default:
      return <Badge variant="secondary">Unknown</Badge>;
  }
};

export function FeatureSupportMatrix({ feature = 'audio', className = '' }: FeatureSupportMatrixProps) {
  const { t } = useTranslation('integrations');

  const supportData = feature === 'audio' ? getAudioMatrixRows() : getAudioMatrixRows();

  const getFeatureTitle = () => {
    switch (feature) {
      case 'audio':
        return t('page.featureSupport.audioTitle');
      case 'text':
        return t('page.featureSupport.textTitle');
      case 'image':
        return t('page.featureSupport.imageTitle');
      case 'video':
        return t('page.featureSupport.videoTitle');
      default:
        return t('page.featureSupport.audioTitle');
    }
  };

  const rowLabel = (rowKey: string) =>
    t(`memoryModalities.matrixRows.${rowKey}`, {
      defaultValue: rowKey,
    });

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">{getFeatureTitle()}</CardTitle>
        <CardDescription>{t('page.featureSupport.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-semibold text-sm">
                  {t('page.featureSupport.columns.integration')}
                </th>
                <th className="text-center p-3 font-semibold text-sm">
                  {t('page.featureSupport.columns.supportLevel')}
                </th>
                <th className="text-center p-3 font-semibold text-sm">
                  {t('page.featureSupport.columns.status')}
                </th>
                <th className="text-left p-3 font-semibold text-sm">
                  {t('page.featureSupport.columns.notes')}
                </th>
              </tr>
            </thead>
            <tbody>
              {supportData.map((item) => (
                <tr key={item.rowKey} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-3 font-medium">{rowLabel(item.rowKey)}</td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      {getSupportIcon(item.support)}
                      {getSupportBadge(item.support, t)}
                    </div>
                  </td>
                  <td className="p-3 text-center">{getStatusBadge(item.status, t)}</td>
                  <td className="p-3 text-sm text-muted-foreground">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help underline decoration-dotted">
                            {item.notes?.substring(0, 50)}
                            {item.notes && item.notes.length > 50 ? '...' : ''}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md">
                          <p>{item.notes}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 pt-4 border-t">
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>{t('page.featureSupport.legend.fullSupport')}</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-500" />
              <span>{t('page.featureSupport.legend.limitedSupport')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span>{t('page.featureSupport.legend.planned')}</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span>{t('page.featureSupport.legend.blocked')}</span>
            </div>
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-gray-400" />
              <span>{t('page.featureSupport.legend.notSupported')}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default FeatureSupportMatrix;
